import React, { useState } from "react";
import useSWR from "swr";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Spinner, StatusBadge, KV, Empty } from "@/components/Atoms";
import { fmtDate, fmtRel } from "@/lib/format";
import { Copy, Plus, ShieldX, Trash2, LifeBuoy, ExternalLink, Save, ShieldCheck } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function EntitlementsCard({ id, reseller, onSaved }) {
    const { data: catalog } = useSWR("/api/admin/resellers/entitlements/catalog", fetcher);
    const [busy, setBusy] = useState(null);
    const [err, setErr] = useState(null);
    const granted = reseller.entitlements || [];

    const toggle = async (key) => {
        setBusy(key);
        setErr(null);
        const next = granted.includes(key)
            ? granted.filter((k) => k !== key)
            : [...granted, key];
        try {
            await api.patch(`/api/admin/resellers/${id}`, { entitlements: next });
            onSaved && (await onSaved());
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="cm-card p-5" data-testid="reseller-entitlements-card">
            <div className="flex items-center gap-2 mb-1">
                <ShieldCheck size={15} className="text-[#CCFF00]" />
                <div className="cm-h3">Granted Services (Entitlements)</div>
            </div>
            <p className="text-xs text-zinc-500 mb-4">
                Premium services this reseller may enable on their clouds.
                Without a grant, the service is hidden in the cloud service panel and
                activation is rejected server-side.
            </p>
            <div className="space-y-2">
                {(catalog || []).map((ent) => {
                    const on = granted.includes(ent.key);
                    return (
                        <label
                            key={ent.key}
                            className="flex items-start gap-3 bg-cm-bg border border-cm-line p-3 cursor-pointer hover:border-zinc-700"
                            data-testid={`entitlement-${ent.key}`}
                        >
                            <input
                                type="checkbox"
                                className="mt-0.5 accent-[#CCFF00]"
                                checked={on}
                                disabled={busy === ent.key}
                                onChange={() => toggle(ent.key)}
                                data-testid={`entitlement-${ent.key}-toggle`}
                            />
                            <span className="flex-1">
                                <span className="flex items-center gap-2">
                                    <span className="font-mono text-sm text-zinc-200">{ent.label}</span>
                                    <span
                                        className={`cm-badge text-[10px] ${on ? "cm-badge-success" : "cm-badge-neutral"}`}
                                        data-testid={`entitlement-${ent.key}-state`}
                                    >
                                        {on ? "GRANTED" : "DENIED"}
                                    </span>
                                </span>
                                <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{ent.description}</div>
                            </span>
                        </label>
                    );
                })}
                {catalog && catalog.length === 0 && (
                    <div className="text-xs text-zinc-500">No entitlements defined.</div>
                )}
            </div>
            {err && <div className="text-xs text-cm-danger mt-3" data-testid="entitlements-err">{err}</div>}
        </div>
    );
}

function SupportContactCard({ id, reseller, onSaved }) {
    const [form, setForm] = useState({
        support_name: reseller.support_name || "",
        support_email: reseller.support_email || "",
        support_url: reseller.support_url || "",
        support_phone: reseller.support_phone || "",
    });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [err, setErr] = useState(null);

    const save = async () => {
        setSaving(true);
        setErr(null);
        setSaved(false);
        try {
            await api.patch(`/api/admin/resellers/${id}`, form);
            setSaved(true);
            onSaved && onSaved();
            setTimeout(() => setSaved(false), 2500);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const fields = [
        ["support_name", "Support name / company", "e.g. Acme Hosting Support"],
        ["support_email", "Support email", "help@acme.de"],
        ["support_url", "Support-URL", "acme.de/support"],
        ["support_phone", "Support phone", "+49 30 123456"],
    ];

    return (
        <div className="cm-card p-5" data-testid="reseller-support-card">
            <div className="flex items-center gap-2 mb-1">
                <LifeBuoy size={15} className="text-[#CCFF00]" />
                <div className="cm-h3">Support contact (error page)</div>
            </div>
            <p className="text-xs text-zinc-500 mb-4">
                These details appear on the "Cloud could not be created" page
                shown to end customers when a cloud provisioning fails.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {fields.map(([k, label, ph]) => (
                    <div key={k}>
                        <label className="cm-label">{label}</label>
                        <input
                            data-testid={`support-${k}`}
                            className="cm-input mt-1"
                            value={form[k]}
                            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                            placeholder={ph}
                        />
                    </div>
                ))}
            </div>
            {err && <div className="text-xs text-cm-danger mt-3">{err}</div>}
            <div className="flex flex-wrap items-center gap-2 mt-4">
                <button
                    data-testid="support-save-button"
                    className="cm-btn-primary text-xs flex items-center gap-1.5"
                    onClick={save}
                    disabled={saving}
                >
                    <Save size={12} />
                    {saving ? "Saving…" : saved ? "Saved ✓" : "Save"}
                </button>
                <a
                    data-testid="support-preview-error"
                    className="cm-btn-ghost text-xs flex items-center gap-1.5"
                    href={`${BACKEND}/api/admin/lb/error-preview?reseller_id=${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <ExternalLink size={12} /> Error page preview
                </a>
                <a
                    data-testid="support-preview-splash"
                    className="cm-btn-ghost text-xs flex items-center gap-1.5"
                    href={`${BACKEND}/api/admin/lb/placeholder-preview`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <ExternalLink size={12} /> Splash preview
                </a>
            </div>
        </div>
    );
}

export default function ResellerDetailPage() {
    const { id } = useParams();
    const { data: reseller, mutate: refetchReseller } = useSWR(`/api/admin/resellers/${id}`, fetcher);
    const { data: tokens, mutate: refetchTokens } = useSWR(
        `/api/admin/resellers/${id}/tokens`,
        fetcher,
    );
    const { data: scopes } = useSWR("/api/admin/scopes", fetcher);
    const { data: clouds } = useSWR(`/api/admin/clouds?reseller_id=${id}`, fetcher);

    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        name: "",
        scopes: ["clouds:read"],
        expires_in_days: "",
    });
    const [createdToken, setCreatedToken] = useState(null);
    const [err, setErr] = useState(null);
    const [statusBusy, setStatusBusy] = useState(false);

    if (!reseller) return <div className="p-8"><Spinner /></div>;

    const submit = async (e) => {
        e.preventDefault();
        setErr(null);
        try {
            const { data } = await api.post(
                `/api/admin/resellers/${id}/tokens`,
                {
                    name: form.name,
                    scopes: form.scopes,
                    expires_in_days: form.expires_in_days ? Number(form.expires_in_days) : null,
                },
            );
            setCreatedToken(data);
            setForm({ name: "", scopes: ["clouds:read"], expires_in_days: "" });
            setShowForm(false);
            await refetchTokens();
        } catch (e) {
            setErr(formatApiError(e));
        }
    };

    const revoke = async (tokenId) => {
        await api.post(`/api/admin/resellers/${id}/tokens/${tokenId}/revoke`);
        refetchTokens();
    };
    const delTok = async (tokenId) => {
        await api.delete(`/api/admin/resellers/${id}/tokens/${tokenId}`);
        refetchTokens();
    };
    // Suspend/resume the whole reseller. status="suspended" makes the auth layer
    // reject EVERY API token of this reseller with 403 "Reseller inactive"
    // (core/auth.get_api_principal) — an instant, reversible kill-switch for all
    // its API keys at once. Existing clouds keep running; only API access stops.
    const toggleStatus = async () => {
        const suspend = reseller.status === "active";
        if (!window.confirm(suspend
            ? `Suspend reseller "${reseller.name}"?\n\nAll ${reseller.tokens_count} API token(s) will be rejected with 403 immediately. `
              + `Existing clouds keep running, but the reseller can no longer use the API. Reversible.`
            : `Resume reseller "${reseller.name}"?\n\nThe API token(s) will work again afterwards.`)) return;
        setStatusBusy(true); setErr(null);
        try {
            await api.patch(`/api/admin/resellers/${id}`,
                            { status: suspend ? "suspended" : "active" });
            await refetchReseller();
        } catch (e) { setErr(formatApiError(e)); }
        setStatusBusy(false);
    };

    return (
        <div data-testid="reseller-detail-page">
            <PageHeader
                testid="reseller-detail-header"
                back="/resellers"
                backLabel="Resellers"
                title={reseller.name}
                subtitle={
                    <span className="font-mono text-xs">
                        {reseller.slug} · {reseller.id}
                    </span>
                }
                action={
                    <div className="flex items-center gap-2">
                        <StatusBadge status={reseller.status} />
                        <button
                            data-testid="reseller-toggle-status"
                            className={(reseller.status === "active"
                                ? "cm-btn-danger" : "cm-btn-primary")
                                + " text-xs flex items-center gap-1.5"}
                            disabled={statusBusy}
                            onClick={toggleStatus}
                            title={reseller.status === "active"
                                ? "Suspend reseller — all API tokens rejected immediately"
                                : "Resume reseller — API tokens valid again"}
                        >
                            {reseller.status === "active"
                                ? (<><ShieldX size={12} /> Suspend</>)
                                : (<><ShieldCheck size={12} /> Resume</>)}
                        </button>
                    </div>
                }
            />
            <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-800">
                <div className="cm-card p-5">
                    <div className="cm-h3 mb-3">Profile</div>
                    <KV k="Name" v={reseller.name} />
                    <KV k="Slug" v={<span className="font-mono">{reseller.slug}</span>} />
                    <KV k="Contact" v={reseller.contact_email || "—"} />
                    <KV k="Status" v={<StatusBadge status={reseller.status} />} />
                    <KV k="Created" v={fmtDate(reseller.created_at)} />
                    <KV k="Clouds" v={<span className="font-mono">{reseller.clouds_count}</span>} />
                    <KV k="Tokens" v={<span className="font-mono">{reseller.tokens_count}</span>} />
                </div>

                <div className="lg:col-span-2 cm-card p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="cm-h3">API tokens</div>
                        <button
                            data-testid="tokens-new-button"
                            className="cm-btn-primary text-xs flex items-center gap-1.5"
                            onClick={() => setShowForm((v) => !v)}
                        >
                            <Plus size={12} />
                            New token
                        </button>
                    </div>

                    {showForm && (
                        <form onSubmit={submit} className="bg-cm-bg border border-cm-line p-4 mb-4 space-y-3 cm-fade-in">
                            <div>
                                <label className="cm-label">Name</label>
                                <input
                                    data-testid="tokens-form-name"
                                    className="cm-input mt-1"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder="e.g. Acme CI"
                                    required
                                />
                            </div>
                            <div>
                                <label className="cm-label">Scopes</label>
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    {(scopes || []).map((s) => (
                                        <label
                                            key={s.scope}
                                            className="flex items-start gap-2 text-xs cursor-pointer hover:text-cm-text"
                                        >
                                            <input
                                                type="checkbox"
                                                className="mt-0.5"
                                                data-testid={`scope-${s.scope}`}
                                                checked={form.scopes.includes(s.scope)}
                                                onChange={(e) => {
                                                    setForm((f) => ({
                                                        ...f,
                                                        scopes: e.target.checked
                                                            ? [...f.scopes, s.scope]
                                                            : f.scopes.filter((x) => x !== s.scope),
                                                    }));
                                                }}
                                            />
                                            <span>
                                                <span className="font-mono text-zinc-300">{s.scope}</span>
                                                <div className="text-[11px] text-zinc-500">{s.description}</div>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="cm-label">Expires in (days)</label>
                                <input
                                    data-testid="tokens-form-expires"
                                    type="number"
                                    className="cm-input mt-1 max-w-[180px]"
                                    value={form.expires_in_days}
                                    onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })}
                                    placeholder="(empty = never)"
                                />
                            </div>
                            {err && <div className="text-xs text-cm-danger">{err}</div>}
                            <button
                                type="submit"
                                className="cm-btn-primary"
                                data-testid="tokens-form-submit"
                            >
                                Mint token
                            </button>
                        </form>
                    )}

                    {createdToken && (
                        <div
                            className="bg-cm-brand/5 border border-cm-brand/40 p-4 mb-4 cm-fade-in"
                            data-testid="tokens-created-banner"
                        >
                            <div className="cm-label text-cm-brand mb-2">
                                Token issued — copy now, it will not be shown again
                            </div>
                            <div className="font-mono text-xs break-all text-zinc-100 bg-cm-bg border border-cm-line p-3 flex items-start justify-between gap-3">
                                <span data-testid="tokens-created-value">{createdToken.plaintext_token}</span>
                                <button
                                    className="cm-btn-ghost text-xs flex items-center gap-1 shrink-0"
                                    onClick={() => navigator.clipboard.writeText(createdToken.plaintext_token)}
                                    data-testid="tokens-copy-button"
                                >
                                    <Copy size={12} /> Copy
                                </button>
                            </div>
                            <button
                                className="cm-btn-ghost text-xs mt-2"
                                onClick={() => setCreatedToken(null)}
                            >
                                Dismiss
                            </button>
                        </div>
                    )}

                    {!tokens?.length ? (
                        <Empty title="No tokens" hint="Mint a token to enable API access for this reseller." />
                    ) : (
                        <div className="cm-table-wrap">
                        <table className="cm-table">
                            <thead>
                                <tr>
                                    <th className="cm-th">Name</th>
                                    <th className="cm-th">Prefix</th>
                                    <th className="cm-th">Scopes</th>
                                    <th className="cm-th">Status</th>
                                    <th className="cm-th">Last used</th>
                                    <th className="cm-th"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {tokens.map((t) => (
                                    <tr key={t.id} data-testid={`token-row-${t.id}`}>
                                        <td className="cm-td">{t.name}</td>
                                        <td className="cm-td font-mono text-xs text-zinc-400">{t.token_prefix}</td>
                                        <td className="cm-td">
                                            <div className="flex flex-wrap gap-1">
                                                {t.scopes.map((s) => (
                                                    <span
                                                        key={s}
                                                        className="cm-badge cm-badge-info text-[10px]"
                                                    >
                                                        {s}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="cm-td">
                                            <StatusBadge
                                                status={t.revoked ? "suspended" : "active"}
                                            />
                                        </td>
                                        <td className="cm-td text-xs text-zinc-500">
                                            {t.last_used_at ? fmtRel(t.last_used_at) : "never"}
                                        </td>
                                        <td className="cm-td text-right">
                                            {!t.revoked && (
                                                <button
                                                    className="cm-btn-ghost text-xs flex items-center gap-1"
                                                    onClick={() => revoke(t.id)}
                                                    data-testid={`token-revoke-${t.id}`}
                                                >
                                                    <ShieldX size={12} /> Revoke
                                                </button>
                                            )}
                                            <button
                                                className="cm-btn-ghost text-xs flex items-center gap-1 text-cm-danger"
                                                onClick={() => delTok(t.id)}
                                                data-testid={`token-delete-${t.id}`}
                                            >
                                                <Trash2 size={12} /> Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    )}
                </div>
            </div>

            <div className="p-4 sm:p-6 pt-0">
                <SupportContactCard
                    id={id}
                    reseller={reseller}
                    onSaved={() => refetchReseller()}
                />
            </div>

            <div className="p-4 sm:p-6 pt-0">
                <EntitlementsCard
                    id={id}
                    reseller={reseller}
                    onSaved={() => refetchReseller()}
                />
            </div>

            <div className="p-4 sm:p-6 pt-0">
                <div className="cm-h3 mb-3">Clouds owned by {reseller.name}</div>
                {!clouds?.length ? (
                    <Empty title="No clouds for this reseller yet" />
                ) : (
                    <div className="cm-card overflow-hidden">
                        <div className="cm-table-wrap">
                        <table className="cm-table">
                            <thead>
                                <tr>
                                    <th className="cm-th">Name</th>
                                    <th className="cm-th">Domain</th>
                                    <th className="cm-th">Status</th>
                                    <th className="cm-th">Storage</th>
                                    <th className="cm-th">Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clouds.map((c) => (
                                    <tr key={c.id} className="cm-tr" data-testid={`reseller-cloud-${c.id}`}>
                                        <td className="cm-td">
                                            <Link className="text-cm-text hover:text-cm-brand" to={`/clouds/${c.id}`}>
                                                {c.name}
                                            </Link>
                                        </td>
                                        <td className="cm-td font-mono text-xs text-zinc-400">
                                            {c.primary_domain || "—"}
                                        </td>
                                        <td className="cm-td">
                                            <StatusBadge status={c.status} />
                                        </td>
                                        <td className="cm-td font-mono">{c.storage_gb} GB</td>
                                        <td className="cm-td text-xs text-zinc-500">{fmtRel(c.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
